// utils/relatedLists.js
//
// RELATED LISTS ON A CARD (epic KPE-029; keepr-api docs/SCHEMA.md, Card
// definition, `relatedLists`). A card defines named lists of the items that
// LINK TO the item being viewed; the item view shows them after its first,
// fixed entry, Related items. This module is what the card page's editor
// knows about them: the stored shape, which cards and lookups may be chosen,
// the checks the API will make anyway (said before the round trip where the
// client can be sure), and where a refusal lands.
//
// The API decides every rule (400 `invalid_options` naming the list and the
// field, 400 `too_many_related_lists`). Nothing here is a second authority:
// a check that depends on stored data this page cannot see whole (does a
// lookup exist that points here?) is advice, never a refusal.

import _ from 'lodash';

import { normalizeId } from './ids';
import { mergeList } from './reapply';
import { getCollectionFamilyCardIds } from './collections';

/** The first switcher entry. Not stored, always first, and not a name a list may take. */
export const RELATED_ITEMS_NAME = 'Related items';

/** keepr-api `LIMITS.nameLength` for a list's name. */
export const LIST_NAME_MAX = 80;

/** Used until the contract says otherwise (`limits.relatedListsPerCard`). */
export const DEFAULT_LISTS_PER_CARD = 20;

/** The entry's keys, exactly: the API refuses any other. `sort` (KPR-493) only when the list has an order. */
export const LIST_KEYS = Object.freeze(['id', 'name', 'sourceCardId', 'viaElementId', 'filter', 'hidden', 'sort']);

/**
 * Element types with no order a person means (keepr-api
 * `UNSORTABLE_TYPES`): a list is never ordered by one, and the API refuses it
 * (400 `invalid_options`, `field: 'sort'`).
 */
export const UNSORTABLE_TYPES = Object.freeze(['rich-text', 'file', 'repeating-group']);

const ELEMENT_ID = /^[a-z0-9]{8}$/;

/** A list's order as stored: `{ element, direction }`, or null (none, or not well formed). */
export function sortOf(list) {
	const sort = list?.sort;
	if (!sort || typeof sort !== 'object' || !ELEMENT_ID.test(`${sort.element || ''}`)) return null;
	return { element: sort.element, direction: sort.direction === 'desc' ? 'desc' : 'asc' };
}

/** `list` with `sort` set to `next`, or without the key when `next` is none — a list with no order stores none. */
export function withSort(list, next) {
	const { sort, ...rest } = list || {}; // eslint-disable-line no-unused-vars
	const order = sortOf({ sort: next });
	return order ? { ...rest, sort: order } : rest;
}

/**
 * The source card's elements a list may be ordered by: saved ones (an id to
 * store) of a type with an order, in the card's own order.
 */
export function sortCandidates({ sourceCardId, getElementsForCard }) {
	if (!sourceCardId || typeof getElementsForCard !== 'function') return [];
	return (getElementsForCard(normalizeId(sourceCardId)) || [])
		.filter(element => ELEMENT_ID.test(`${element?.id || ''}`) && element?.name && !UNSORTABLE_TYPES.includes(element.dataType));
}

/**
 * The element a list's order names, among the source card's elements: by id,
 * or a copy of it (an element set's "Copy into this card" gives the copy a new
 * id and stamps the old one as `derivedFrom`, KPR-735). Null when none: the
 * list then reads in the default order.
 */
export function sortElementOf(elements, elementId) {
	if (!elementId || !Array.isArray(elements)) return null;
	return elements.find(element => element?.id === elementId)
		|| elements.find(element => element?.derivedVia === elementId)
		|| elements.find(element => element?.derivedFrom === elementId)
		|| null;
}

/**
 * A new list. Its `id` is a draft id (the API mints the real one on Save), so
 * the row keeps one identity while it is edited, moved and removed around it.
 */
let draftCounter = 0;
export const DRAFT_ID_PREFIX = 'draft-';
export const newDraftId = () => `${DRAFT_ID_PREFIX}${++draftCounter}`;
export const isDraftId = id => typeof id === 'string' && id.startsWith(DRAFT_ID_PREFIX);
export const blankList = () => ({ id: newDraftId(), name: '', sourceCardId: '', viaElementId: null, filter: null, hidden: false });

/** The lists as the card PATCH sends them: a draft id goes as a blank one, which the API replaces. */
export const forSave = lists => (Array.isArray(lists)
	? lists.map(list => (isDraftId(list?.id) ? { ...list, id: '' } : list))
	: lists);

/** An entry as the API stores it: the six keys, `sort` when it has one, nothing served alongside. */
export const storedShape = (list) => {
	const sort = sortOf(list);
	return {
		id: typeof list?.id === 'string' ? list.id : '',
		name: typeof list?.name === 'string' ? list.name : '',
		sourceCardId: normalizeId(list?.sourceCardId) || '',
		viaElementId: list?.viaElementId || null,
		filter: typeof list?.filter === 'string' && list.filter.trim() ? list.filter : null,
		hidden: list?.hidden === true,
		...(sort ? { sort } : {}),
	};
};

const isObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** The card's OWN lists, as stored (`options.relatedLists`). */
export const ownListsOf = card => (Array.isArray(card?.options?.relatedLists)
	? card.options.relatedLists.filter(isObject).map(storedShape)
	: []);

/**
 * The lists the card inherits, root first, from the read-only `relatedLists`
 * the card definition reads add (each `{ ..., cardId, tier, inherited }`).
 */
export const inheritedListsOf = card => (Array.isArray(card?.relatedLists)
	? card.relatedLists.filter(list => isObject(list) && list.inherited === true)
	: []);

/** Names compare the way the API compares them: trimmed, spaces collapsed, case ignored. */
export const nameKey = name => `${name ?? ''}`.trim().replace(/\s+/g, ' ').toLowerCase();

/** What the API will store of a typed name. */
export const tidyName = name => `${name ?? ''}`.trim().replace(/\s+/g, ' ');

// ---------------------------------------------------------------- the choices

const parentOf = (card, cardsById) => cardsById?.[normalizeId(card?.parentCardId)] || null;

/** A card and its ancestors, as a set of ids: what a lookup must point at to hold one of the card's items. */
export function cardLine(cardId, cardsById) {
	const out = new Set();
	let current = cardsById?.[normalizeId(cardId)] || { _id: cardId };
	while (current) {
		const id = normalizeId(current._id);
		if (!id || out.has(id)) break;
		out.add(id);
		current = parentOf(current, cardsById);
	}
	return out;
}

/**
 * The cards a list may be built from: the collection's cards and those of its
 * sub-collections (and their descendants), and the card itself (sub-tasks).
 * The same family the API admits (`admittedSource`), judged on the cards the
 * page has; the API's answer on Save is the final one.
 */
export function sourceCardsFor({ collection, cards = [], cardId = null, keepIds = [] } = {}) {
	const list = Array.isArray(cards) ? cards : [];
	const byId = new Map(list.map(card => [normalizeId(card?._id), card]).filter(([id]) => id));
	const family = collection ? getCollectionFamilyCardIds(collection, list) : new Set();
	const own = normalizeId(cardId);
	if (own) family.add(own);
	const reaches = (card) => {
		const seen = new Set();
		let current = card;
		while (current) {
			const id = normalizeId(current._id);
			if (!id || seen.has(id)) return false;
			if (family.has(id)) return true;
			seen.add(id);
			current = byId.get(normalizeId(current.parentCardId));
		}
		return false;
	};
	const keep = new Set((keepIds || []).map(normalizeId).filter(Boolean));
	return list.filter((card) => {
		const id = normalizeId(card?._id);
		return Boolean(id) && (reaches(card) || keep.has(id));
	});
}

/** Can this element hold an item of a card whose line is `line`? A lookup to that card, an ancestor, or to no card. */
export function pointsHere(element, line) {
	if (element?.dataType !== 'card-lookup') return false;
	const to = normalizeId(element?.options?.lookupCardId);
	return !to || line.has(to);
}

/**
 * The lookups of the source card (its own, inherited and its element sets')
 * the via picker offers: those that can hold an item of this card. A lookup to
 * another card, or to a card beneath this one, never holds one of its items
 * (keepr-api: 400 `invalid_options` naming the element), so it is not offered.
 * Only a saved element has an id to store.
 */
export function viaCandidates({ sourceCardId, line, getElementsForCard }) {
	if (!sourceCardId || typeof getElementsForCard !== 'function') return [];
	return (getElementsForCard(sourceCardId) || [])
		.filter(element => typeof element?.id === 'string' && element.id && pointsHere(element, line));
}

/**
 * Does the source card (its own elements, its ancestors' and its sets') hold a
 * lookup that points here? That is what the API's write gate asks of a list
 * with no lookup chosen, and refuses with 400 `invalid_options` when none does.
 */
export function sourceHasLink({ sourceCardId, line, getElementsForCard }) {
	if (!sourceCardId || typeof getElementsForCard !== 'function') return false;
	return (getElementsForCard(normalizeId(sourceCardId)) || []).some(element => pointsHere(element, line));
}

// ------------------------------------------------------------ what Save will say

/**
 * The first thing wrong with the draft that the client can be certain of, in
 * the shape the API refuses with — so it lands in the same place:
 * `{ code, key, list, field, message }`. A name and a source card are
 * required; the name may not be the first entry's, be too long, or repeat
 * another list's the card shows. `saved` is the card's stored lists. As the
 * API judges only what a save changes, a stored list is judged on its name
 * only when the name changed, and on its source card only when that changed:
 * an old list that predates a rule (or clashes with a parent's newer list)
 * must not refuse an unrelated edit.
 */
export function firstListProblem(lists, { inheritedNames = [], saved = [] } = {}) {
	const taken = new Set((inheritedNames || []).map(nameKey));
	const seen = new Set();
	const storedById = new Map((saved || []).filter(entry => entry.id).map(entry => [entry.id, entry]));
	for (let index = 0; index < lists.length; index++) {
		const list = lists[index];
		const name = tidyName(list.name);
		const at = { code: 'invalid_options', key: 'relatedLists', list: index };
		const stored = list.id ? storedById.get(list.id) : null;
		const nameChanged = !stored || tidyName(stored.name) !== name;
		const sourceChanged = !stored || normalizeId(stored.sourceCardId) !== normalizeId(list.sourceCardId);
		const key = nameKey(name);
		if (nameChanged) {
			if (!name) return { ...at, field: 'name', message: 'Name the list.' };
			if (name.length > LIST_NAME_MAX) return { ...at, field: 'name', message: `A name is at most ${LIST_NAME_MAX} characters.` };
			if (key === nameKey(RELATED_ITEMS_NAME)) return { ...at, field: 'name', message: `${RELATED_ITEMS_NAME} is the list every item already shows first. Choose another name.` };
			if (taken.has(key) || seen.has(key)) return { ...at, field: 'name', message: `Another list is already called “${name}”.` };
		}
		if (name) seen.add(key);
		if (sourceChanged && !normalizeId(list.sourceCardId)) return { ...at, field: 'sourceCardId', message: 'Choose the card whose items this list shows.' };
	}
	return null;
}

/** A refusal of the API (or of `firstListProblem`) that is about one of the card's related lists. */
export const isRelatedListsRefusal = coded => Boolean(coded) && coded.key === 'relatedLists';

/** The refusal for list `index`, as `{ field, message }`, or null. Position in the card's own array, as the API counts. */
export function refusalForList(saveError, index) {
	if (!isRelatedListsRefusal(saveError)) return null;
	if (typeof saveError.list !== 'number' || saveError.list !== index) return null;
	return { field: saveError.field || 'list', message: saveError.message || 'This list could not be saved.' };
}

/** The refusal about the lists as a whole (the limit), or null. */
export function limitRefusal(saveError) {
	return isRelatedListsRefusal(saveError) && saveError.code === 'too_many_related_lists' ? saveError : null;
}

/** The fields of a coded 400 the editor keeps to place a related-list refusal. */
export function relatedListRefusalFields(data) {
	if (!isObject(data) || data.key !== 'relatedLists') return {};
	return {
		key: 'relatedLists',
		...(typeof data.list === 'number' ? { list: data.list } : {}),
		...(typeof data.listId === 'string' ? { listId: data.listId } : {}),
		...(typeof data.field === 'string' ? { field: data.field } : {}),
		...(typeof data.name === 'string' ? { name: data.name } : {}),
		...(typeof data.cardId === 'string' ? { cardId: data.cardId } : {}),
		...(typeof data.limit === 'number' ? { limit: data.limit } : {}),
	};
}

// ------------------------------------------------------------------- the draft

/** Moves list `from` to `to` (own lists only), or returns the same array. */
export function moveList(lists, from, to) {
	if (from === to || from < 0 || to < 0 || from >= lists.length || to >= lists.length) return lists;
	const next = [...lists];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved);
	return next;
}

/** Is the draft list the stored one in everything the count read depends on (source, via, filter)? */
export function sameReading(draft, saved) {
	if (!draft || !saved) return false;
	return normalizeId(draft.sourceCardId) === normalizeId(saved.sourceCardId)
		&& (draft.viaElementId || null) === (saved.viaElementId || null)
		&& (draft.filter || null) === (saved.filter || null);
}

/**
 * A stale save's reapply for the lists (utils/reapply.js merges `options` one
 * key deep, so the lists would be mine whole): mine in my order, a colleague's
 * new list appended, one they removed gone when I left it alone.
 */
export function mergeOwnLists(base, mine, theirs) {
	const list = value => (Array.isArray(value) ? value.filter(isObject) : []);
	return mergeList(list(base), list(mine), list(theirs), entry => entry.id);
}

/**
 * The names of the lists BOTH people changed, differently, since the form
 * loaded: a reapply keeps mine over theirs there, and the dialog says so.
 */
export function overlappingLists(base, mine, theirs) {
	const list = value => (Array.isArray(value) ? value.filter(isObject) : []);
	const byId = items => new Map(list(items).filter(entry => entry.id).map(entry => [entry.id, entry]));
	const was = byId(base);
	const theirsById = byId(theirs);
	return list(mine)
		.filter((entry) => {
			const before = was.get(entry.id);
			const them = theirsById.get(entry.id);
			return Boolean(before && them) && !_.isEqual(entry, before) && !_.isEqual(them, before) && !_.isEqual(entry, them);
		})
		.map(entry => tidyName(entry.name) || 'A list');
}

// ------------------------------------------------------------ a list that cannot read

const STATE_WORDS = Object.freeze({
	removed: 'The lookup this list uses was removed from the source card.',
	not_a_lookup: 'The element this list uses is no longer a lookup.',
	points_elsewhere: 'The lookup this list uses now points at another card.',
	no_lookup: 'No lookup on the source card points at this card now.',
	source_removed: 'The source card has been removed.',
	filter_unreadable: 'The default filter can no longer be read, so the list shows nothing.',
});

/** `X-Related-List-State` in words; a state keepr does not name is said plainly. */
export const describeListState = state => (state ? (STATE_WORDS[state] || 'This list cannot find items right now.') : '');

/**
 * `relatedListsAffected` (a card or element-set save that removes, retypes or
 * re-points a lookup a list reads): one sentence per list, in the API's words.
 */
export function affectedSentences(affected) {
	if (!Array.isArray(affected)) return [];
	return affected
		.filter(isObject)
		.map((entry) => {
			const where = entry.cardName ? ` on ${entry.cardName}` : '';
			const name = entry.listName ? `“${entry.listName}”${where}` : 'A related list';
			return `${name}: ${entry.message || 'it can no longer find items.'}`;
		});
}
